/**
 * Comprehensive filter mappings for Schema.org structured data
 * Maps B-Stock filter values to their proper Schema.org equivalents
 */

// Condition mappings (already exists, but included for completeness)
export const mapConditionToSchemaOrg = (condition: string): string => {
  const conditionMap: Record<string, string> = {
    New: 'https://schema.org/NewCondition',
    'Brand New': 'https://schema.org/NewCondition',
    'Like New': 'https://schema.org/NewCondition',
    Refurbished: 'https://schema.org/RefurbishedCondition',
    Used: 'https://schema.org/UsedCondition',
    'Used Good': 'https://schema.org/UsedCondition',
    'Used Fair': 'https://schema.org/UsedCondition',
    'Used A': 'https://schema.org/UsedCondition',
    'Used B': 'https://schema.org/UsedCondition',
    'Used C': 'https://schema.org/UsedCondition',
    'Used D': 'https://schema.org/UsedCondition',
    'Used E': 'https://schema.org/UsedCondition',
    'Scratch & Dent': 'https://schema.org/DamagedCondition',
    'Scratch & Dent Appliances': 'https://schema.org/DamagedCondition',
    Salvage: 'https://schema.org/DamagedCondition',
    Mixed: 'https://schema.org/UsedCondition',
    'Mixed Condition': 'https://schema.org/UsedCondition',
    'Used - Fair': 'https://schema.org/UsedCondition',
    'Used - Good': 'https://schema.org/UsedCondition',
    'Device Grade A': 'https://schema.org/UsedCondition',
    'Device Grade B': 'https://schema.org/UsedCondition',
    'Device Grade C': 'https://schema.org/UsedCondition',
    'Device Grade D': 'https://schema.org/UsedCondition',
    'Device Grade E': 'https://schema.org/UsedCondition',
  }

  return conditionMap[condition] || 'https://schema.org/UsedCondition'
}

// Category mappings to Schema.org product categories
export const mapCategoryToSchemaOrg = (category: string): string => {
  const categoryMap: Record<string, string> = {
    'Apparel Shoes & Accessories': 'https://schema.org/Clothing',
    'Books Movies & Music': 'https://schema.org/Book',
    'Toys Kids & Baby': 'https://schema.org/Toy',
    Electronics: 'https://schema.org/Electronics',
    'Computers & Tablets': 'https://schema.org/Computer',
    'Cell Phones & Accessories': 'https://schema.org/MobilePhone',
    'Home & Garden': 'https://schema.org/HomeGoods',
    'Sports & Outdoors': 'https://schema.org/SportingGoods',
    Automotive: 'https://schema.org/Automotive',
    'Health & Beauty': 'https://schema.org/HealthAndBeautyBusiness',
    'Jewelry & Watches': 'https://schema.org/JewelryStore',
    'Tools & Hardware': 'https://schema.org/HardwareStore',
    'Office Products': 'https://schema.org/OfficeEquipmentStore',
    'Pet Supplies': 'https://schema.org/PetStore',
    'Food & Beverages': 'https://schema.org/FoodEstablishment',
    'Industrial & Scientific': 'https://schema.org/IndustrialBusiness',
    'Musical Instruments': 'https://schema.org/MusicStore',
    'Art & Collectibles': 'https://schema.org/ArtGallery',
    Antiques: 'https://schema.org/Antique',
    Furniture: 'https://schema.org/FurnitureStore',
    Appliances: 'https://schema.org/ApplianceStore',
    'Garden & Outdoor': 'https://schema.org/GardenStore',
    'Kitchen & Dining': 'https://schema.org/KitchenSupplyStore',
    Bedding: 'https://schema.org/BeddingStore',
    Bath: 'https://schema.org/BathroomSupplyStore',
    'Storage & Organization': 'https://schema.org/StorageFacility',
    Lighting: 'https://schema.org/LightingStore',
    'Rugs & Mats': 'https://schema.org/FlooringStore',
    'Window Treatments': 'https://schema.org/WindowTreatment',
    'Wall Art': 'https://schema.org/ArtGallery',
    'Decorative Accessories': 'https://schema.org/HomeDecor',
    'Seasonal Decor': 'https://schema.org/SeasonalBusiness',
    'Party Supplies': 'https://schema.org/PartySupplyStore',
    Wedding: 'https://schema.org/WeddingBusiness',
    'Baby Products': 'https://schema.org/BabyStore',
    'Kids Clothing': 'https://schema.org/ChildrensClothingStore',
    'Kids Shoes': 'https://schema.org/ChildrensClothingStore',
    'Kids Toys': 'https://schema.org/ToyStore',
    'Kids Books': 'https://schema.org/BookStore',
    'Kids Electronics': 'https://schema.org/ElectronicsStore',
    'Kids Furniture': 'https://schema.org/FurnitureStore',
    'Kids Bedding': 'https://schema.org/BeddingStore',
    'Kids Bath': 'https://schema.org/BathroomSupplyStore',
    'Kids Storage': 'https://schema.org/StorageFacility',
    'Kids Lighting': 'https://schema.org/LightingStore',
    'Kids Rugs': 'https://schema.org/FlooringStore',
    'Kids Wall Art': 'https://schema.org/ArtGallery',
    'Kids Decorative': 'https://schema.org/HomeDecor',
    'Kids Seasonal': 'https://schema.org/SeasonalBusiness',
    'Kids Party': 'https://schema.org/PartySupplyStore',
    'Kids Wedding': 'https://schema.org/WeddingBusiness',
  }

  return categoryMap[category] || 'https://schema.org/Product'
}

// Region mappings to Schema.org location types
export const mapRegionToSchemaOrg = (region: string): string => {
  const regionMap: Record<string, string> = {
    'United States': 'https://schema.org/Country',
    Canada: 'https://schema.org/Country',
    Europe: 'https://schema.org/Continent',
    Asia: 'https://schema.org/Continent',
    'South America': 'https://schema.org/Continent',
    Africa: 'https://schema.org/Continent',
    Australia: 'https://schema.org/Continent',
    'North America': 'https://schema.org/Continent',
    // US Regions
    Northeast: 'https://schema.org/Region',
    Southeast: 'https://schema.org/Region',
    Midwest: 'https://schema.org/Region',
    Southwest: 'https://schema.org/Region',
    West: 'https://schema.org/Region',
    Northwest: 'https://schema.org/Region',
    // Individual states (abbreviated)
    AL: 'https://schema.org/State',
    AK: 'https://schema.org/State',
    AZ: 'https://schema.org/State',
    AR: 'https://schema.org/State',
    CA: 'https://schema.org/State',
    CO: 'https://schema.org/State',
    CT: 'https://schema.org/State',
    DE: 'https://schema.org/State',
    FL: 'https://schema.org/State',
    GA: 'https://schema.org/State',
    HI: 'https://schema.org/State',
    ID: 'https://schema.org/State',
    IL: 'https://schema.org/State',
    IN: 'https://schema.org/State',
    IA: 'https://schema.org/State',
    KS: 'https://schema.org/State',
    KY: 'https://schema.org/State',
    LA: 'https://schema.org/State',
    ME: 'https://schema.org/State',
    MD: 'https://schema.org/State',
    MA: 'https://schema.org/State',
    MI: 'https://schema.org/State',
    MN: 'https://schema.org/State',
    MS: 'https://schema.org/State',
    MO: 'https://schema.org/State',
    MT: 'https://schema.org/State',
    NE: 'https://schema.org/State',
    NV: 'https://schema.org/State',
    NH: 'https://schema.org/State',
    NJ: 'https://schema.org/State',
    NM: 'https://schema.org/State',
    NY: 'https://schema.org/State',
    NC: 'https://schema.org/State',
    ND: 'https://schema.org/State',
    OH: 'https://schema.org/State',
    OK: 'https://schema.org/State',
    OR: 'https://schema.org/State',
    PA: 'https://schema.org/State',
    RI: 'https://schema.org/State',
    SC: 'https://schema.org/State',
    SD: 'https://schema.org/State',
    TN: 'https://schema.org/State',
    TX: 'https://schema.org/State',
    UT: 'https://schema.org/State',
    VT: 'https://schema.org/State',
    VA: 'https://schema.org/State',
    WA: 'https://schema.org/State',
    WV: 'https://schema.org/State',
    WI: 'https://schema.org/State',
    WY: 'https://schema.org/State',
    // Full state names
    Alabama: 'https://schema.org/State',
    Alaska: 'https://schema.org/State',
    Arizona: 'https://schema.org/State',
    Arkansas: 'https://schema.org/State',
    California: 'https://schema.org/State',
    Colorado: 'https://schema.org/State',
    Connecticut: 'https://schema.org/State',
    Delaware: 'https://schema.org/State',
    Florida: 'https://schema.org/State',
    Georgia: 'https://schema.org/State',
    Hawaii: 'https://schema.org/State',
    Idaho: 'https://schema.org/State',
    Illinois: 'https://schema.org/State',
    Indiana: 'https://schema.org/State',
    Iowa: 'https://schema.org/State',
    Kansas: 'https://schema.org/State',
    Kentucky: 'https://schema.org/State',
    Louisiana: 'https://schema.org/State',
    Maine: 'https://schema.org/State',
    Maryland: 'https://schema.org/State',
    Massachusetts: 'https://schema.org/State',
    Michigan: 'https://schema.org/State',
    Minnesota: 'https://schema.org/State',
    Mississippi: 'https://schema.org/State',
    Missouri: 'https://schema.org/State',
    Montana: 'https://schema.org/State',
    Nebraska: 'https://schema.org/State',
    Nevada: 'https://schema.org/State',
    'New Hampshire': 'https://schema.org/State',
    'New Jersey': 'https://schema.org/State',
    'New Mexico': 'https://schema.org/State',
    'New York': 'https://schema.org/State',
    'North Carolina': 'https://schema.org/State',
    'North Dakota': 'https://schema.org/State',
    Ohio: 'https://schema.org/State',
    Oklahoma: 'https://schema.org/State',
    Oregon: 'https://schema.org/State',
    Pennsylvania: 'https://schema.org/State',
    'Rhode Island': 'https://schema.org/State',
    'South Carolina': 'https://schema.org/State',
    'South Dakota': 'https://schema.org/State',
    Tennessee: 'https://schema.org/State',
    Texas: 'https://schema.org/State',
    Utah: 'https://schema.org/State',
    Vermont: 'https://schema.org/State',
    Virginia: 'https://schema.org/State',
    Washington: 'https://schema.org/State',
    'West Virginia': 'https://schema.org/State',
    Wisconsin: 'https://schema.org/State',
    Wyoming: 'https://schema.org/State',
  }

  return regionMap[region] || 'https://schema.org/Place'
}

// Inventory type mappings
export const mapInventoryTypeToSchemaOrg = (inventoryType: string): string => {
  const inventoryTypeMap: Record<string, string> = {
    Overstock: 'https://schema.org/Overstock',
    Returns: 'https://schema.org/Returned',
    Liquidation: 'https://schema.org/Liquidation',
    Closeout: 'https://schema.org/Closeout',
    Refurbished: 'https://schema.org/Refurbished',
    Used: 'https://schema.org/Used',
    New: 'https://schema.org/New',
    Damaged: 'https://schema.org/Damaged',
    Salvage: 'https://schema.org/Salvage',
    Mixed: 'https://schema.org/Mixed',
    'Customer Returns': 'https://schema.org/Returned',
    'Store Returns': 'https://schema.org/Returned',
    'Manufacturer Returns': 'https://schema.org/Returned',
    'Warranty Returns': 'https://schema.org/Returned',
    Defective: 'https://schema.org/Defective',
    'Open Box': 'https://schema.org/OpenBox',
    'Display Model': 'https://schema.org/DisplayModel',
    'Floor Model': 'https://schema.org/DisplayModel',
    Demo: 'https://schema.org/Demo',
    Test: 'https://schema.org/Test',
    Sample: 'https://schema.org/Sample',
  }

  return inventoryTypeMap[inventoryType] || 'https://schema.org/Product'
}

// Listing type mappings
export const mapListingTypeToSchemaOrg = (listingType: string): string => {
  const listingTypeMap: Record<string, string> = {
    Auction: 'https://schema.org/Auction',
    'Buy Now': 'https://schema.org/BuyNow',
    Offer: 'https://schema.org/Offer',
    'Fixed Price': 'https://schema.org/FixedPrice',
    'Dutch Auction': 'https://schema.org/DutchAuction',
    'English Auction': 'https://schema.org/EnglishAuction',
    'Sealed Bid': 'https://schema.org/SealedBid',
    'Reverse Auction': 'https://schema.org/ReverseAuction',
    Lot: 'https://schema.org/Lot',
    Bundle: 'https://schema.org/Bundle',
    'Single Item': 'https://schema.org/SingleItem',
    'Multiple Items': 'https://schema.org/MultipleItems',
  }

  return listingTypeMap[listingType] || 'https://schema.org/Offer'
}

// Buying format mappings
export const mapBuyingFormatToSchemaOrg = (buyingFormat: string): string => {
  const buyingFormatMap: Record<string, string> = {
    Auction: 'https://schema.org/Auction',
    'Buy Now': 'https://schema.org/BuyNow',
    'Make Offer': 'https://schema.org/Offer',
    'Best Offer': 'https://schema.org/Offer',
    'Fixed Price': 'https://schema.org/FixedPrice',
    Negotiable: 'https://schema.org/Negotiable',
    Wholesale: 'https://schema.org/Wholesale',
    Retail: 'https://schema.org/Retail',
    Bulk: 'https://schema.org/Bulk',
    Lot: 'https://schema.org/Lot',
    Pallet: 'https://schema.org/Pallet',
    Container: 'https://schema.org/Container',
    Truckload: 'https://schema.org/Truckload',
  }

  return buyingFormatMap[buyingFormat] || 'https://schema.org/Offer'
}

// Transport mode mappings
export const mapTransportModeToSchemaOrg = (transportMode: string): string => {
  const transportModeMap: Record<string, string> = {
    Pickup: 'https://schema.org/Pickup',
    Delivery: 'https://schema.org/Delivery',
    Shipping: 'https://schema.org/Shipping',
    Freight: 'https://schema.org/Freight',
    LTL: 'https://schema.org/LTL',
    FTL: 'https://schema.org/FTL',
    'Local Pickup': 'https://schema.org/LocalPickup',
    'Local Delivery': 'https://schema.org/LocalDelivery',
    'White Glove': 'https://schema.org/WhiteGlove',
    'Inside Delivery': 'https://schema.org/InsideDelivery',
    'Lift Gate': 'https://schema.org/LiftGate',
    'Residential Delivery': 'https://schema.org/ResidentialDelivery',
    'Commercial Delivery': 'https://schema.org/CommercialDelivery',
  }

  return transportModeMap[transportMode] || 'https://schema.org/DeliveryMethod'
}

// Inspection mappings
export const mapInspectionToSchemaOrg = (inspection: string): string => {
  const inspectionMap: Record<string, string> = {
    Available: 'https://schema.org/Available',
    'Not Available': 'https://schema.org/NotAvailable',
    'By Appointment': 'https://schema.org/ByAppointment',
    Scheduled: 'https://schema.org/Scheduled',
    'Open House': 'https://schema.org/OpenHouse',
    Virtual: 'https://schema.org/Virtual',
    'In Person': 'https://schema.org/InPerson',
    Remote: 'https://schema.org/Remote',
    Video: 'https://schema.org/Video',
    Photo: 'https://schema.org/Photo',
    Documentation: 'https://schema.org/Documentation',
  }

  return inspectionMap[inspection] || 'https://schema.org/Availability'
}

// Allowed shipment types mappings
export const mapShipmentTypeToSchemaOrg = (shipmentType: string): string => {
  const shipmentTypeMap: Record<string, string> = {
    Pickup: 'https://schema.org/Pickup',
    Delivery: 'https://schema.org/Delivery',
    Shipping: 'https://schema.org/Shipping',
    Freight: 'https://schema.org/Freight',
    LTL: 'https://schema.org/LTL',
    FTL: 'https://schema.org/FTL',
    'Local Pickup': 'https://schema.org/LocalPickup',
    'Local Delivery': 'https://schema.org/LocalDelivery',
    'White Glove': 'https://schema.org/WhiteGlove',
    'Inside Delivery': 'https://schema.org/InsideDelivery',
    'Lift Gate': 'https://schema.org/LiftGate',
    'Residential Delivery': 'https://schema.org/ResidentialDelivery',
    'Commercial Delivery': 'https://schema.org/CommercialDelivery',
    International: 'https://schema.org/International',
    Domestic: 'https://schema.org/Domestic',
  }

  return shipmentTypeMap[shipmentType] || 'https://schema.org/DeliveryMethod'
}

/**
 * Generic mapping function that routes to the appropriate mapper based on filter type
 * @param filterType The type of filter (e.g., 'condition', 'category', 'region')
 * @param value The filter value to map
 * @returns The mapped Schema.org value
 */
export const mapFilterToSchemaOrg = (
  filterType: string,
  value: string
): string => {
  switch (filterType.toLowerCase()) {
    case 'condition':
      return mapConditionToSchemaOrg(value)
    case 'category':
    case 'categories':
      return mapCategoryToSchemaOrg(value)
    case 'region':
    case 'regions':
      return mapRegionToSchemaOrg(value)
    case 'inventorytype':
    case 'inventory_type':
      return mapInventoryTypeToSchemaOrg(value)
    case 'listingtype':
    case 'listing_type':
      return mapListingTypeToSchemaOrg(value)
    case 'buyingformat':
    case 'buying_format':
      return mapBuyingFormatToSchemaOrg(value)
    case 'transportmode':
    case 'transport_mode':
      return mapTransportModeToSchemaOrg(value)
    case 'inspection':
      return mapInspectionToSchemaOrg(value)
    case 'allowedshipmenttypes':
    case 'allowed_shipment_types':
    case 'shipmenttype':
    case 'shipment_type':
      return mapShipmentTypeToSchemaOrg(value)
    default:
      return value // Return original value if no mapping exists
  }
}
